import { useEffect, useMemo, useState } from 'react';

import { fg } from '@atlaskit/platform-feature-flags/fg';
import { addUFOCustomCohortData } from '@atlaskit/react-ufo/custom-cohort-data';
import {
	type ConfigCacheState,
	type OnOAuthSuccessCallback,
	toConfigCacheState,
	useRovoConfig,
} from '@atlassian/rovo-connectors-config/hooks/useRovoConfig';
import type { ProductKeys3P } from '@atlassian/rovo-connectors-config/types';
import { buildAtlAttributionHeaders } from '@atlassian/search-client/atl-attribution';
import {
	getCombinedSearchConfig,
	type SearchCombinedUnitMetadata,
	seedCombinedConfigCacheEntry,
} from '@atlassian/search-client/config';
import { type DegradationCallback } from '@atlassian/search-client/degradable-promise';
import { type TSearchConfigAPIResponse } from '@atlassian/search-client/fetch-search-config';
import { type CacheAnalyticsCallback } from '@atlassian/search-client/persist-refresh';
import { searchLinkedSitesQuery } from '@atlassian/search-client/search-page';
import { type LinkedSite } from '@atlassian/search-client/search-page/types';

import { EVENT_SOURCE } from '../../common/constants/analytics';
import { type NounKeys, nouns as supportedNouns } from '../../common/constants/nouns';
import { type PrimaryProductKey, ProductKeys } from '../../common/constants/product-keys';
import { useProductConfigs } from '../../common/constants/products';
import {
	type ForgeConnectorProductKey,
	type ThirdPartyConfigsBootstrap,
	type ThirdPartyConfigsWithAPIResponse,
} from '../../common/constants/schemas/3p-config';
import type { SearchStoreExperience } from '../../common/constants/search';
import { type SearchCombinedConfigPayload } from '../../common/types/search-combined-config';
import { type SearchPreloadDataProvider } from '../../common/types/search-preload';
import { onDegradedDependency } from '../../common/utils/events/degraded-dependency';
import { onFailedDependency } from '../../common/utils/events/failed-dependency';
import { onRequestCacheAccess } from '../../common/utils/events/on-cache-access';
import { TASK_NAMES } from '../../common/utils/events/task-names';
import {
	onTaskFail,
	onTaskStart,
	onTaskSuccess,
} from '../../common/utils/events/vital-stats-events';
import { registerForgeConnectorTypeMapping } from '../../common/utils/filters/constants';
import { createVitalStatsNetworkCallbacks } from '../../common/utils/network-task-tracker';
import { useAnalytics } from '../analytics';
import { useLogException } from '../error-boundary/utils';
import { useSearchSessionId } from '../store/bootstrap';

import { adaptCombinedConfigToLegacy } from './combined-config-adapter';
import {
	getFirstCloudId,
	getJiraIssueIndexedForTenant,
	getProductDataRegions,
	getProductEdition,
	getRovoFeatures,
	isRovoEnabled,
} from './site-metadata-helpers';
import { parseCombinedConfig } from './utils/parse-combined-config';
import { map3pResourcesToProductKeys } from './utils/third-party-products';

const EXTRACT_PRODUCT = /ari:cloud:(.*)?::/;

const EMPTY_RESPONSE: (ProductKeys3P | ForgeConnectorProductKey)[] = [];

export type UseFetchConfigProps = {
	/**
	 * @deprecated This prop will be removed in a future major release. The combined-config endpoint
	 * sources third-party config alongside first-party config; consumers no longer need to specify
	 * a separate absolute URL for the third-party fetch.
	 */
	thirdPartyAbsoluteUrl?: string;
	/**
	 * @deprecated This prop will be removed in a future major release. The combined-config endpoint
	 * always fetches third-party config; consumers should opt out at a different layer if needed.
	 */
	thirdPartyDisabled?: boolean;
	userId?: string | null;
	cloudId?: string;
	aggAbsoluteUrl?: string;
	primaryProduct?: PrimaryProductKey;
	/**
	 * When passed this would be used as is to invoke the endpoint which returns search-configuration i.e. list of products accessible by the logged in user.
	 * This is useful for products like Trello where the API gateway is not on the host domain, but a completely different URL.
	 *
	 * No need to pass if the UI is being bootstrapped inside Jira, Confluence or any product tied to these sites.
	 */
	configFetchAbsoluteUrl?: string;
	aggregatorHostName?: string;
	multiSiteSearchInRovo?: boolean;
	experience?: SearchStoreExperience;
	activationId?: string;
	preloadDataProvider?: SearchPreloadDataProvider;
};

/**
 * Public `useFetchConfig` hook consumed by `BootstrapStoreProvider` and
 * (transitively) the rest of the search-page bootstrap pipeline.
 *
 * RAOC-22: this hook is now a thin **dispatcher** between two
 * implementations, gated by `rovo_fps_use_rovo_config`:
 *
 * The FG dispatch lives **inside** the public hook (not at the call
 * site) so that consumers see a single, stable `useFetchConfig`
 * interface and rules-of-hooks are upheld: each branch is itself a
 * hook, and the FG value is stable for the component lifetime
 * (Statsig SDK semantics).
 */
const reportConfigCacheState = (state: ConfigCacheState): void => {
	addUFOCustomCohortData('configCacheState', state);
};

export const useFetchConfig: (props: UseFetchConfigProps) => UseFetchConfigReturn = (props) => {
	// These return the same type and are like for like replacements, this is the easiest way to safely FG without generating extra network calls.
	/* eslint-disable react-hooks/rules-of-hooks */
	return fg('rovo_fps_use_rovo_config')
		? useFetchConfigFromRovoConfig(props)
		: useFetchConfigLegacy(props);
	/* eslint-enable react-hooks/rules-of-hooks */
};

/**
 * Return type shared by `useFetchConfigLegacy` and
 * `useFetchConfigFromRovoConfig`. Both branches must produce the exact
 * same shape so that the public `useFetchConfig` dispatcher (and all of
 * its downstream consumers) can be branch-agnostic.
 */
export type UseFetchConfigReturn = {
	accessibleProducts: string[] | undefined;
	accessibleNouns: NounKeys[];
	thirdPartyConfigs: Partial<Record<string, ThirdPartyConfigsWithAPIResponse>> | undefined;
	firstCloudId: string;
	isRovoEnabled: boolean | undefined;
	isJiraIssueIndexed: boolean | undefined;
	productEdition: import('@atlassian/search-client').TProductEdition | undefined;
	productDataRegions: import('@atlassian/search-client').TProductDataRegions | undefined;
	rovoFeatures: import('@atlassian/search-client').TRovoFeatures | undefined;
	isConfigLoaded: boolean;
	linkedCloudARIs: string[] | undefined;
	unitMetadata?: SearchCombinedUnitMetadata[] | undefined;
	onOAuthSuccess: OnOAuthSuccessCallback | undefined;
};

/**
 * Legacy `useFetchConfig` — preserved verbatim (other than the rename)
 * from the pre-RAOC-22-PR-3 implementation. Wrapped by the public
 * `useFetchConfig` above when `rovo_fps_use_rovo_config` is OFF.
 *
 * Will be removed in RAOC-22 PR-4 once the gate ramps to 100%.
 */
export const useFetchConfigLegacy: (props: UseFetchConfigProps) => UseFetchConfigReturn = ({
	cloudId: cloudIdProp,
	configFetchAbsoluteUrl,
	aggregatorHostName,
	userId,
	primaryProduct,
	aggAbsoluteUrl,
	multiSiteSearchInRovo,
	experience,
	activationId,
	preloadDataProvider,
}: UseFetchConfigProps) => {
	const supportedProducts = useProductConfigs();

	// Unnecessary, should be removed to save react cycles
	const [firstPartyResponse, setFirstPartyResponse] = useState<TSearchConfigAPIResponse>();
	const [isConfigLoaded, setIsConfigLoaded] = useState<boolean>(false);

	// Unnecessary, should be removed to save react cycles
	const [combinedThirdPartyResponse, setCombinedThirdPartyResponse] = useState<{
		productKeys?: (ProductKeys3P | ForgeConnectorProductKey)[];
		thirdPartyConfigs?: ThirdPartyConfigsBootstrap;
	}>({});

	// Rovo Multiple Site Search
	const [linkedCloudARIs, setLinkedCloudARIs] = useState<string[] | undefined>(undefined);

	// Combined Config Response
	const [combinedConfigResponse, setCombinedConfigResponse] =
		useState<SearchCombinedConfigPayload>();

	// Unnecessary, should be removed to save react cycles
	const [accessibleProductsConfig, setAccessibleProductsConfig] = useState<{
		products?: ProductKeys[];
		nouns: NounKeys[];
		thirdPartyConfigs?: ThirdPartyConfigsBootstrap;
	}>({
		nouns: [],
	});
	const { fireAnalyticsEvent } = useAnalytics({ experience });
	const [searchSessionId] = useSearchSessionId();

	const logException = useLogException();

	const firstCloudId = useMemo(() => {
		if (!cloudIdProp && firstPartyResponse) {
			return getFirstCloudId(firstPartyResponse);
		}
		return '';
	}, [firstPartyResponse, cloudIdProp]);

	const thirdPartyResponse = combinedThirdPartyResponse;

	const atlAttributionHeaders = useMemo(
		() =>
			buildAtlAttributionHeaders({
				cloudId: cloudIdProp,
				activationId,
				product: primaryProduct,
			}),
		[cloudIdProp, activationId, primaryProduct],
	);

	// Rovo Multiple Site Search - Store linked site ARIs in state
	useEffect(() => {
		const cloudId = cloudIdProp || firstCloudId;
		if (cloudId && multiSiteSearchInRovo) {
			searchLinkedSitesQuery({
				variables: { id: `ari:cloud:confluence::site/${cloudId}` },
				aggAbsoluteUrl,
				headers: atlAttributionHeaders,
			})
				.then((response) => {
					const edges = response?.data?.collabContext_workspaceIsConnectedToWorkspace?.edges ?? [];
					const linkedARIs = edges.map((edge: LinkedSite) => edge.node.id);

					// Move current cloudId to index 0 if it exists and isn't already there
					// This is a temporary solution for multiple site search in rovo with correct CloudIdARIs sequence
					const currentCloudIdARI = `ari:cloud:confluence::site/${cloudId}`;
					const currentIndex = linkedARIs.findIndex((ari) => ari === currentCloudIdARI);

					if (currentIndex > 0) {
						// Remove from current position and add to beginning
						linkedARIs.splice(currentIndex, 1);
						linkedARIs.unshift(currentCloudIdARI);
					}

					setLinkedCloudARIs(linkedARIs);
				})
				.catch((error) => {
					logException(error);
				});
		}
	}, [
		cloudIdProp,
		firstCloudId,
		multiSiteSearchInRovo,
		logException,
		aggAbsoluteUrl,
		atlAttributionHeaders,
	]);

	useEffect(() => {
		if (!firstPartyResponse) {
			if (!cloudIdProp) {
				return;
			}

			if (combinedThirdPartyResponse.productKeys) {
				return;
			}

			/**
			 * Combined config caching strategy shared across both:
			 * - network fetch path (`getCombinedSearchConfig`)
			 * - SSR-preloaded network seed path (`seedCombinedConfigCacheEntry`)
			 *
			 * Strategy:
			 * - refresh only when cached data is expired
			 * - return cached value even if expired
			 */
			const combinedConfigTTL = 1000 * 60 * 1; // 1 minute

			const fetchCombinedConfig = () => {
				const uxTaskId = crypto.randomUUID();
				const taskStartAt = performance.now();

				const baseUXVitalStatsParams = {
					searchSessionId,
					taskId: uxTaskId,
					task: TASK_NAMES.SEARCH_PAGE_COMBINED_CONFIG_UX,
				};

				// DegradedDependency may not be visible to the end-user due to request caching. This is a health metric for our BE
				const degradationCallback: DegradationCallback = (reason) => {
					fireAnalyticsEvent(
						onDegradedDependency({
							dependency: 'search-combined-config',
							searchSessionId,
							reason,
							source: EVENT_SOURCE,
						}),
					);
				};

				const cacheAnalyticsCallback: CacheAnalyticsCallback = async (state) => {
					reportConfigCacheState(toConfigCacheState(state));

					fireAnalyticsEvent(
						onRequestCacheAccess({
							requestName: 'search-combined-config',
							searchSessionId,
							cacheHit: state.hit,
							refreshTriggered: state.refreshTriggered,
							source: EVENT_SOURCE,
						}),
					);
				};

				// Create network callbacks for tracking network requests
				const configNetworkTaskId = crypto.randomUUID();
				const networkCallbacks = createVitalStatsNetworkCallbacks(
					configNetworkTaskId,
					TASK_NAMES.SEARCH_PAGE_COMBINED_CONFIG_NETWORK,
					searchSessionId,
					fireAnalyticsEvent,
					taskStartAt,
					{ uxTaskId },
				);

				const searchConfigParams = {
					cloudId: cloudIdProp,
					userId,
					aggAbsoluteUrl,
					degradationCallback,
					cacheAnalyticsCallback,
					expiration: combinedConfigTTL,
					returnExpired: true,
					onlyRefreshIfExpired: true,
					networkCallbacks,
					headers: atlAttributionHeaders,
				};

				// Fire task start for UX tracking
				fireAnalyticsEvent(onTaskStart(baseUXVitalStatsParams));

				getCombinedSearchConfig(searchConfigParams)
					.then((configuration: SearchCombinedConfigPayload) => {
						setCombinedConfigResponse(configuration);
						const { firstPartyConfig, thirdPartyConfig } = adaptCombinedConfigToLegacy(
							configuration,
							cloudIdProp,
						);
						setFirstPartyResponse(firstPartyConfig);
						setCombinedThirdPartyResponse(map3pResourcesToProductKeys(thirdPartyConfig));

						// Fire user experience metric - user got data (success from UX perspective)
						fireAnalyticsEvent(
							onTaskSuccess({
								...baseUXVitalStatsParams,
								moreContext: {
									timeTaken: performance.now() - taskStartAt,
								},
							}),
						);
					})
					.catch((error: Error) => {
						logException(error);

						// FailedDependency is visible to the end-user. This is a health metric for our UX
						fireAnalyticsEvent(
							onFailedDependency({
								dependency: 'search-combined-config',
								searchSessionId,
								source: EVENT_SOURCE,
							}),
						);

						// Fire user experience metric - user did not get data (failure from UX perspective)
						fireAnalyticsEvent(
							onTaskFail({
								...baseUXVitalStatsParams,
								moreContext: {
									error: error.name,
									timeTaken: performance.now() - taskStartAt,
								},
							}),
						);

						setFirstPartyResponse({
							resources: [],
							// Unused by search-page — `TSearchConfigAPIResponse` still requires the field.
							intercomHmac: '',
							siteMetadata: [],
						});
						setCombinedThirdPartyResponse({
							productKeys: EMPTY_RESPONSE,
							thirdPartyConfigs: {},
						});
					});
			};

			// We skip trying to access the config from the preload provider if
			// the preload came from Quick Find. The reason for this is that the
			// combined config call will have already been cached in memory and
			// there's no reason to pipe it through the preload provider.
			let isPreloadFromQuickFind = false;
			try {
				const preloadedSearch = preloadDataProvider?.getPreloadedSearch({
					cloudId: cloudIdProp,
				});
				isPreloadFromQuickFind = Boolean(
					preloadedSearch && preloadedSearch.source === 'quick-find',
				);
			} catch {
				// Reading the preload provider should never throw, but be defensive
				// — a bad lookup must not block config loading.
			}

			if (preloadDataProvider && !isPreloadFromQuickFind) {
				try {
					const preloadedSearch = preloadDataProvider.getPreloadedSearch({
						cloudId: cloudIdProp,
					});
					if (preloadedSearch && preloadedSearch.config) {
						const preloadUxTaskId = crypto.randomUUID();
						const preloadTaskStartAt = performance.now();
						const preloadMoreContext = {
							isSSREvent: true,
							isConfigFromCache: Boolean(preloadedSearch.isConfigFromCache),
						};
						const preloadBaseUXVitalStatsParams = {
							searchSessionId,
							taskId: preloadUxTaskId,
							task: TASK_NAMES.SEARCH_PAGE_COMBINED_CONFIG_UX,
							moreContext: preloadMoreContext,
						};
						fireAnalyticsEvent(onTaskStart(preloadBaseUXVitalStatsParams));

						try {
							setCombinedConfigResponse(preloadedSearch.config);
							const { firstPartyConfig, thirdPartyConfig } = adaptCombinedConfigToLegacy(
								preloadedSearch.config,
								cloudIdProp,
							);
							setFirstPartyResponse(firstPartyConfig);
							setCombinedThirdPartyResponse(map3pResourcesToProductKeys(thirdPartyConfig));
							if (!preloadedSearch.isConfigFromCache) {
								void seedCombinedConfigCacheEntry({
									cloudId: cloudIdProp,
									userId,
									config: preloadedSearch.config,
									expiration: combinedConfigTTL,
								}).catch((seedError: unknown) => {
									logException(seedError);
								});
							}
							fireAnalyticsEvent(
								onTaskSuccess({
									...preloadBaseUXVitalStatsParams,
									moreContext: {
										...preloadMoreContext,
										timeTaken: performance.now() - preloadTaskStartAt,
									},
								}),
							);
							if (preloadedSearch.isConfigFromCache) {
								// Preload gives immediate UI data, but we still run the normal combined-config
								// path so cache TTL rules can trigger a network refresh when the entry is expired.
								fetchCombinedConfig();
							}
							return; // Code smell, code is poorly structured, difficult to glean logic
						} catch (error: unknown) {
							logException(error);
							fireAnalyticsEvent(
								onTaskFail({
									...preloadBaseUXVitalStatsParams,
									moreContext: {
										...preloadMoreContext,
										error: error instanceof Error ? error.name : 'unknown',
										timeTaken: performance.now() - preloadTaskStartAt,
									},
								}),
							);
							fetchCombinedConfig();
							return; // Code smell, code is poorly structured, difficult to glean logic
						}
					}
					if (preloadedSearch && !preloadedSearch.config) {
						fireAnalyticsEvent(
							onFailedDependency({
								dependency: 'search-config-ssr',
								searchSessionId,
								source: EVENT_SOURCE,
								moreContext: {
									cacheFailReason: preloadedSearch.cacheFailReason ?? 'none',
									configFailReason: preloadedSearch.configFailReason ?? 'none',
									preloadPayloadWithoutConfig: true,
								},
							}),
						);
					}
					fetchCombinedConfig();
				} catch (error: unknown) {
					logException(error);
					fetchCombinedConfig();
				}
				return;
			}

			fetchCombinedConfig();
		}
	}, [
		firstPartyResponse,
		combinedThirdPartyResponse.productKeys,
		cloudIdProp,
		userId,
		aggAbsoluteUrl,
		logException,
		fireAnalyticsEvent,
		searchSessionId,
		atlAttributionHeaders,
		preloadDataProvider,
	]);

	useEffect(() => {
		if (
			thirdPartyResponse.productKeys &&
			firstPartyResponse &&
			!accessibleProductsConfig.products
		) {
			let allAccessibleProducts: ProductKeys[] = [];

			const firstPartyProducts = [
				...new Set(
					firstPartyResponse.resources.flatMap((resource) => {
						const { id } = resource;
						const match = id.match(EXTRACT_PRODUCT);

						if (match && match.length >= 2 && match[1] in supportedProducts) {
							return [match[1]];
						}

						return [];
					}),
				),
			];

			if (firstPartyProducts.length === 0 && primaryProduct) {
				// Use the primary product if the 1P Search Config request fails
				allAccessibleProducts.push(primaryProduct as ProductKeys);
			} else {
				allAccessibleProducts.push(...(firstPartyProducts as ProductKeys[]));
			}

			// Always ensure the primary product is in the list, even if the config
			// response doesn't include it (e.g. resources only contain other products).
			// This mirrors the error fallback behavior where the primary product is
			// always available so users can search within their hosting product.
			if (primaryProduct && !allAccessibleProducts.includes(primaryProduct as ProductKeys)) {
				allAccessibleProducts.unshift(primaryProduct as ProductKeys);
			}

			// Goal and Project nouns are now returned via the first party product list in the combined config response.
			const accessibleNouns = firstPartyResponse.resources.flatMap((resource) => {
				const { id } = resource;
				const match = id.match(EXTRACT_PRODUCT);

				if (match && match.length >= 2 && match[1] in supportedNouns) {
					return [match[1] as NounKeys];
				}

				return [];
			});

			// If nouns are available we also need to add Townsquare to the list of accessible products.
			// This way Projects will be fetched by default when no filters are selected.
			if (accessibleNouns.length > 0) {
				allAccessibleProducts.push(ProductKeys.Atlas);
			}

			// with combined config, bitbucket is handled in the BE

			// Allow 3P products only if a user has Rovo enabled.
			// Long-term it won't be needed if the 3p-configuration check ensures Rovo eligibility.
			// See https://hello.atlassian.net/wiki/spaces/SEARCH/pages/4469899124.
			const doesUserHaveRovo = isRovoEnabled(firstPartyResponse, cloudIdProp);
			if (doesUserHaveRovo) {
				const thirdPartyProductKeys = thirdPartyResponse.productKeys || [];
				allAccessibleProducts.push(...(thirdPartyProductKeys as ProductKeys[]));
				setAccessibleProductsConfig({
					products: allAccessibleProducts,
					nouns: accessibleNouns,
					thirdPartyConfigs: thirdPartyResponse.thirdPartyConfigs,
				});
			} else {
				setAccessibleProductsConfig({
					products: allAccessibleProducts,
					nouns: accessibleNouns,
					thirdPartyConfigs: {},
				});
			}
			setIsConfigLoaded(true);
		}
	}, [
		thirdPartyResponse,
		firstPartyResponse,
		setAccessibleProductsConfig,
		primaryProduct,
		fireAnalyticsEvent,
		logException,
		cloudIdProp,
		accessibleProductsConfig.products,
		supportedProducts,
	]);

	const accessibleProducts = accessibleProductsConfig.products;

	const parsedConfigFromNewCombinedConfigParser = useMemo(() => {
		if (primaryProduct && cloudIdProp && combinedConfigResponse) {
			const parsedCombinedConfig = parseCombinedConfig({
				configuration: combinedConfigResponse,
				primaryProduct: primaryProduct,
				cloudId: cloudIdProp,
			});

			return {
				...parsedCombinedConfig,
				firstCloudId: '', // cloudId must always exist when using the combined endpoint, hence we don't infer one
				isConfigLoaded,
				linkedCloudARIs,
				onOAuthSuccess: undefined,
			};
		}

		return null;
	}, [primaryProduct, cloudIdProp, combinedConfigResponse, isConfigLoaded, linkedCloudARIs]);

	if (parsedConfigFromNewCombinedConfigParser) {
		return parsedConfigFromNewCombinedConfigParser;
	}

	return {
		accessibleProducts,
		accessibleNouns: accessibleProductsConfig.nouns,
		thirdPartyConfigs: accessibleProductsConfig.thirdPartyConfigs,
		firstCloudId,
		isRovoEnabled: isRovoEnabled(firstPartyResponse, cloudIdProp),
		isJiraIssueIndexed: getJiraIssueIndexedForTenant(firstPartyResponse, cloudIdProp),
		productEdition: getProductEdition(firstPartyResponse, cloudIdProp),
		productDataRegions: getProductDataRegions(firstPartyResponse, cloudIdProp),
		rovoFeatures: getRovoFeatures(firstPartyResponse, cloudIdProp),
		isConfigLoaded,
		linkedCloudARIs,
		onOAuthSuccess: undefined,
	};
};

/**
 * `useRovoConfig`-backed implementation of `useFetchConfig` — active when
 * `rovo_fps_use_rovo_config` is ON.
 *
 * The shared `useRovoConfig` hook in `@atlassian/rovo-connectors-config`
 * is the canonical source of truth for every combined-config-derived
 * field. This wrapper:
 *
 *   1. Forwards the relevant subset of `UseFetchConfigProps` to
 *      `useRovoConfig`, plus the analytics dependencies
 *      (`fireAnalyticsEvent`, `logException`, `searchSessionId`) that
 *      were previously sourced inside the legacy hook body.
 *   2. Applies the search-page-local primary-product fallback to
 *      `accessibleProducts`. No other field is touched.
 *   3. Drops `combinedConfigResponse` (which `useRovoConfig` exposes for
 *      callers that want the raw payload) so the return shape exactly
 *      matches `UseFetchConfigReturn`.
 *
 * The primary-product fallback is the only behaviour that did NOT move
 * into the shared package, because it is a search-page-specific concern
 * (the shared hook always inserts the primary product if missing, which
 * matches this fallback). This branch could fold into a direct
 * `useRovoConfig` call in future.
 */
export const useFetchConfigFromRovoConfig: (props: UseFetchConfigProps) => UseFetchConfigReturn = ({
	cloudId,
	primaryProduct,
	aggAbsoluteUrl,
	multiSiteSearchInRovo,
	preloadDataProvider,
}) => {
	const rovoConfig = useRovoConfig({
		cloudId,
		aggAbsoluteUrl,
		multiSiteSearchInRovo,
		// `SearchPreloadDataProvider` (search-page) is a structural
		// superset of `RovoConfigPreloadProvider`
		// (rovo-connectors-config). Both have the same
		// `getPreloadedSearch` / `clearPreloadedSearch` contract; the
		// search-page provider just exposes more fields on
		// `SearchPreloadData` than the narrow `RovoConfigPreloadData`
		// type references.
		preloadDataProvider: preloadDataProvider satisfies
			| Parameters<typeof useRovoConfig>[0]['preloadDataProvider']
			| undefined,
		shouldBypassPreloadProvider: (preloaded) => preloaded?.source === 'quick-find',
		onForgeConnectorTypeMapping: registerForgeConnectorTypeMapping,
		onConfigCacheState: reportConfigCacheState,
	});

	return useMemo(() => {
		// `intercomHmac` is still returned by the shared rovo-connectors-config hook but
		// is no longer consumed by search-page, so it is dropped here.
		const {
			combinedConfigResponse: _ignored,
			intercomHmac: _intercomHmac,
			accessibleProducts,
			...rest
		} = rovoConfig;
		const list = accessibleProducts ?? [];

		/**
		 * Primary product fallback logic:
		 * - If `primaryProduct` is not set or config has not loaded, do nothing (return the list as-is, even if empty).
		 * - Otherwise, ensure the primary product is in the list (add it if missing).
		 */
		if (!primaryProduct || !rest.isConfigLoaded) {
			return { ...rest, accessibleProducts };
		}
		if (list.includes(primaryProduct)) {
			return { ...rest, accessibleProducts: list };
		}
		return { ...rest, accessibleProducts: [primaryProduct, ...list] };
	}, [rovoConfig, primaryProduct]);
};
